import { iCamera, iImage, iBox } from '@hover/icons';
import React from 'react';
import { Link as GatsbyLink } from 'gatsby';
import { StaticImage } from 'gatsby-plugin-image';
import { Body, Link } from '@hover/blueprint';
import MeasureIconActive from '../assets/svgs/MeasureIconActive';
import type { IProductInfo } from '../interfaces/IProductInfo';
import type { IProductFeature } from '../interfaces/IProductFeature';
import type { ILearningCardProps } from '../components/common/learning-card';

export const PRODUCTS_INFO: Array<IProductInfo> = [
  {
    id: 0,
    icon: iCamera,
    title: 'Snap a Few Photos',
    body: 'Use our mobile app to take photos of your home from several positions. We’ll automatically upload them to the cloud and take it from there.',
  },
  {
    id: 1,
    icon: iImage,
    title: 'Transform Your Photos',
    body: 'Using our patented technology, we transform your photos into a fully measured and customizable 3D model. We’ll let you know when it’s ready.',
  },
  {
    id: 2,
    svgIcon: <MeasureIconActive />,
    title: 'Review Measurements',
    body: 'Access all roof and siding measurements within the 3D model in our app or online. Or export via PDF, Excel, or other file formats.',
  },
  {
    id: 3,
    icon: iBox,
    title: 'Design in 3D',
    body: 'Change the roof, siding, and windows using real materials from brands like GAF, JamesHardie, and Alside. See the finished project with our fully shareable 3D model.',
  },
];

export const PRODUCT_FEATURES: Array<IProductFeature> = [
  {
    id: 0,
    title: 'Measurements',
    content: (
      <Body mb={500} pr={500}>
        ‘To-the-inch’ accuracy on all{' '}
        <Link as={GatsbyLink} to="/reports/" fontWeight={400}>
          complete exterior measurements
        </Link>{' '}
        including all roofing, siding, windows, surface areas, and openings.
      </Body>
    ),
  },
  {
    id: 1,
    title: 'Interactive 3D Model',
    content: (
      <Body mb={500} pr={500}>
        All the measurements and drawings of each elevation a home improvement
        professional needs. Soffit, fascia, and flashing are all easily viewed
        leading to faster, more accurate and transparent estimations.
      </Body>
    ),
  },
  {
    id: 2,
    title: 'Property Portfolio',
    content: (
      <Body mb={500} pr={500}>
        Take pictures in any order and capture as many as you like. Review and
        edit before you submit to build the 3D model. Document your property
        with free photo storage.
      </Body>
    ),
  },
  {
    id: 3,
    title: 'Photo Capture & Storage',
    content: (
      <Body mb={500} pr={500}>
        Take pictures in any order and capture as many as you like. Review and
        edit before you submit to build the 3D model. Document your property
        with free photo storage.
      </Body>
    ),
  },
  {
    id: 4,
    title: 'Interactive Design',
    content: (
      <Body mb={500} pr={500}>
        Try new finishes and colors in a precise 3D model of your home. Add
        stone facade, special accents like shakes in a gable, replace stucco or
        explore new color combinations.
      </Body>
    ),
  },
  {
    id: 5,
    title: 'Single or Multi-Family Homes',
    content: (
      <Body mb={500} pr={500}>
        HOVER works for single-family homes, townhomes, and multi-family
        residential properties. Tight lot lines, urban or rural areas even with
        some vegetation or obstructions.
      </Body>
    ),
  },
  {
    id: 6,
    title: 'HOVER Prospect',
    content: (
      <Body mb={500} pr={500}>
        Capture and store property photos at no cost, and order the measurements
        and 3D model at any time in the future.
      </Body>
    ),
  },
  {
    id: 7,
    title: 'HOVER Connect',
    content: (
      <Body mb={500} pr={500}>
        Invite homeowners to take photos of their own home for a remote estimate
        or simply to get the 3D model and complete measurements before your
        first meeting.{' '}
        <Link
          fontWeight={400}
          href="https://hover.wistia.com/medias/22o3cdkwp9"
        >
          Learn More
        </Link>
      </Body>
    ),
  },
  {
    id: 8,
    title: 'Data Exports',
    content: (
      <Body mb={500} pr={500}>
        Export your measurements to help with estimation in PDF, Excel, JSON or
        XML formats. Export your 3D model into popular CAD types: SKP, DXF, DWG.
        Export all of your photos to your computer.
      </Body>
    ),
  },
  {
    id: 9,
    title: 'Workflow Integrations',
    content: (
      <Body mb={500} pr={500}>
        From CRM to Project Management to Estimation, our integration partners
        help you to streamline your workflow.{' '}
        <Link fontWeight={400} href="/blog/get-done-faster-integrations/">
          See our partner integrations
        </Link>
        .
      </Body>
    ),
  },
  {
    id: 10,
    title: 'Pro Connect',
    content: (
      <Body mb={500} pr={500}>
        Invite anyone in your company with Pro Connect for maximum workflow
        flexibility.
      </Body>
    ),
  },
  {
    id: 11,
    title: 'Multi-Platform Sync',
    content: (
      <Body mb={500} pr={500}>
        Works seamlessly across your computer and smartphone device. Access your
        HOVER data from the field or home office anywhere, anytime, on any Apple
        or Android device.
      </Body>
    ),
  },
  {
    id: 12,
    title: '*NEW* Estimation',
    content: (
      <Body mb={500} pr={500}>
        Accurate estimates for multiple trades in seconds that reflect job
        complexity and custom pricing.
      </Body>
    ),
  },
  {
    id: 13,
    title: '*NEW* Proposals and Contracts',
    content: (
      <Body mb={500} pr={500}>
        Generate proposals in your custom template without manual entry and get
        digital signatures while on site.
      </Body>
    ),
  },
  {
    id: 14,
    title: '*NEW* Production Console',
    content: (
      <Body mb={500} pr={500}>
        All the resources you need to complete a job in a central location, from
        detailed take-offs and contracts to measurements and job economics.
      </Body>
    ),
  },
  {
    id: 15,
    title: '*NEW* Supplier-direct Ordering',
    content: (
      <Body mb={500} pr={500}>
        Seamlessly order from leading suppliers like Beacon Pro+ with one click
        and your material lists automatically populate your cart for easy
        checkout without leaving the HOVER platform.
      </Body>
    ),
  },
  {
    id: 16,
    title: '*NEW* Materials Ordering',
    content: (
      <Body mb={500} pr={500}>
        Precise, pre-populated material lists for multiple trades that reflect
        your unique install preferences for a specific project.
      </Body>
    ),
  },
  {
    id: 17,
    title: '*NEW* Labor Management',
    content: (
      <Body mb={500} pr={500}>
        Generate work orders with a single click that reflect a project’s
        specific installation needs.
      </Body>
    ),
  },
];

export const PRODUCT_THE_RIGHT_TOOL: ILearningCardProps['card'][] = [
  {
    id: 1,
    description:
      'The fastest and simplest way to get measurements and prepare standout bids from blueprints.',
    title: 'NEW CONSTRUCTION SOLUTION',
    linkTitle: 'Learn more',
    linkTo: '/new-construction-solution/',
    titleColor: 'brandNavy.500',
    image: (
      <StaticImage
        placeholder="blurred"
        loading="lazy"
        alt="NEW CONSTRUCTION SOLUTION"
        src="../assets/images/product_the-right-tool-1.jpg"
      />
    ),
    mobileImage: (
      <StaticImage
        placeholder="blurred"
        loading="lazy"
        alt="NEW CONSTRUCTION SOLUTION"
        src="../assets/images/product_the-right-tool-1.jpg"
      />
    ),
  },
  {
    id: 2,
    description:
      'Bring design ideas to life and share them with clients using a beautifully rendered, photo-realistic vision.',
    title: 'RENDERINGS',
    linkTitle: 'Learn more',
    titleColor: 'brandNavy.500',
    linkTo: '/renderings/',
    image: (
      <StaticImage
        placeholder="blurred"
        loading="lazy"
        alt="RENDERINGS"
        src="../assets/images/product_the-right-tool-2.jpg"
      />
    ),
    mobileImage: (
      <StaticImage
        placeholder="blurred"
        loading="lazy"
        alt="RENDERINGS"
        src="../assets/images/product_the-right-tool-2.jpg"
      />
    ),
  },
  {
    id: 3,
    description: 'Place dependable orders in minutes, not hours.',
    title: 'DIRECT ORDERING',
    linkTitle: 'Learn more',
    linkTo: '/direct-ordering/',
    titleColor: 'brandNavy.500',
    image: (
      <StaticImage
        placeholder="blurred"
        loading="lazy"
        alt="DIRECT ORDERING"
        src="../assets/images/product_the-right-tool-3.jpg"
      />
    ),
    mobileImage: (
      <StaticImage
        placeholder="blurred"
        loading="lazy"
        alt="DIRECT ORDERING"
        src="../assets/images/product_the-right-tool-3.jpg"
      />
    ),
  },
];
